import { useEffect, useRef } from 'react';
import { ColorScheme } from '../constants';
import { BackgroundThemeRevision } from 'referral-types';
import { useBodyStyleVersion } from '../../../hooks/useBodyStyleVersion';

const AnimatedParticleBackground = (props: { themeRevision?: Partial<BackgroundThemeRevision> }) => {
  const bgRef = useRef<HTMLCanvasElement | null>(null);
  const fgRef = useRef<HTMLCanvasElement | null>(null);
  const requestRef = useRef<number | null>(null);
  const bodyStyleVersion = useBodyStyleVersion();

  useEffect(() => {
    let particleConfig: ColorRGB;
    let bgGradient1: string, bgGradient2: string, bgGradient3: string;
    const getCssVar = (variableName: string): string => {
      if (typeof window !== 'undefined') {
        return getComputedStyle(document.body).getPropertyValue(variableName).trim();
      }
      return '0 0 0';
    };
    const getConfigVar = (variableName: string): ColorRGB => {
      return getCssVar(variableName)
        .split(' ')
        .map((v) => parseInt(v, 10)) as ColorRGB;
    };

    switch (props.themeRevision?.subTheme) {
      case ColorScheme.inverted:
        particleConfig = getConfigVar('--primary-300');
        bgGradient1 = `rgb(${getCssVar('--secondary-500')})`;
        bgGradient2 = `rgb(${getCssVar('--secondary-400')})`;
        bgGradient3 = `rgb(${getCssVar('--secondary-700')})`;
        break;
      case ColorScheme.primary:
        particleConfig = getConfigVar('--primary-300');
        bgGradient1 = `rgb(${getCssVar('--primary-500')})`;
        bgGradient2 = `rgb(${getCssVar('--primary-400')})`;
        bgGradient3 = `rgb(${getCssVar('--primary-700')})`;
        break;
      case ColorScheme.secondary:
        particleConfig = getConfigVar('--secondary-300');
        bgGradient1 = `rgb(${getCssVar('--secondary-500')})`;
        bgGradient2 = `rgb(${getCssVar('--secondary-400')})`;
        bgGradient3 = `rgb(${getCssVar('--secondary-700')})`;
        break;
      case ColorScheme.tertiary:
        particleConfig = getConfigVar('--tertiary-300');
        bgGradient1 = `rgb(${getCssVar('--tertiary-500')})`;
        bgGradient2 = `rgb(${getCssVar('--tertiary-400')})`;
        bgGradient3 = `rgb(${getCssVar('--tertiary-700')})`;
        break;
      case ColorScheme.default:
      default:
        particleConfig = getConfigVar('--secondary-300');
        bgGradient1 = `rgb(${getCssVar('--primary-900')})`;
        bgGradient2 = `rgb(${getCssVar('--primary-800')})`;
        bgGradient3 = `rgb(${getCssVar('--primary-700')})`;
    }

    // 1. Setup Contexts
    const bgCanvas = bgRef.current;
    const fgCanvas = fgRef.current;
    const bCtx = bgCanvas?.getContext('2d');
    const fCtx = fgCanvas?.getContext('2d');

    // 2. Configuration
    const config = {
      circle: {
        amount: 18,
        layer: 3,
        color: particleConfig,
        alpha: 0.3,
      },
      line: {
        amount: 12,
        layer: 3,
        color: particleConfig,
        alpha: 0.3,
      },
      speed: 0.5,
      angle: 20,
    };

    let wWidth = window.innerWidth;
    let wHeight = window.innerHeight;
    let circles: CircleParticle[] = [];
    let lines: LineParticle[] = [];
    const M = Math;
    const degree = (config.angle / 360) * M.PI * 2;

    // 3. Helper Functions
    const setCanvasSize = () => {
      if (!bgCanvas || !fgCanvas) return;
      wWidth = window.innerWidth;
      wHeight = window.innerHeight;

      bgCanvas.width = wWidth;
      bgCanvas.height = wHeight;
      fgCanvas.width = wWidth;
      fgCanvas.height = wHeight;
    };

    const drawCircle = (x: number, y: number, radius: number, color: ColorRGB, alpha: number): void => {
      if (!fCtx) return;

      const gradient = fCtx.createRadialGradient(x, y, radius, x, y, 0);
      gradient.addColorStop(0, `rgba(${color[0]},${color[1]},${color[2]},${alpha})`);
      gradient.addColorStop(1, `rgba(${color[0]},${color[1]},${color[2]},${alpha - 0.1})`);

      fCtx.beginPath();
      fCtx.arc(x, y, radius, 0, M.PI * 2, true);
      fCtx.fillStyle = gradient;
      fCtx.fill();
    };

    const drawLine = (x: number, y: number, width: number, color: ColorRGB, alpha: number): void => {
      if (!fCtx) return;

      const endX = x + M.sin(degree) * width;
      const endY = y - M.cos(degree) * width;
      const gradient = fCtx.createLinearGradient(x, y, endX, endY);

      gradient.addColorStop(0, `rgba(${color[0]},${color[1]},${color[2]},${alpha})`);
      gradient.addColorStop(1, `rgba(${color[0]},${color[1]},${color[2]},${alpha - 0.1})`);

      fCtx.beginPath();
      fCtx.moveTo(x, y);
      fCtx.lineTo(endX, endY);
      fCtx.lineWidth = 3;
      fCtx.lineCap = 'round';
      fCtx.strokeStyle = gradient;
      fCtx.stroke();
    };

    const drawBack = () => {
      if (!bCtx) return;
      bCtx.clearRect(0, 0, wWidth, wHeight);

      // Gradient 1
      const g1 = bCtx.createRadialGradient(wWidth * 0.3, wHeight * 0.1, 0, wWidth * 0.3, wHeight * 0.1, wWidth * 0.9);
      g1.addColorStop(0, bgGradient1);
      g1.addColorStop(1, 'transparent');

      bCtx.save();
      bCtx.translate(wWidth, 0);
      bCtx.scale(-1, 1);
      bCtx.beginPath();
      bCtx.fillStyle = g1;
      bCtx.fillRect(0, 0, wWidth, wHeight);
      bCtx.restore();

      // Gradient 2
      const g2 = bCtx.createRadialGradient(wWidth * 0.1, wHeight * 0.1, 0, wWidth * 0.3, wHeight * 0.1, wWidth);
      g2.addColorStop(0, bgGradient2);
      g2.addColorStop(0.8, 'transparent');

      bCtx.save();
      bCtx.translate(wWidth, 0);
      bCtx.scale(-1, 1);
      bCtx.beginPath();
      bCtx.fillStyle = g2;
      bCtx.fillRect(0, 0, wWidth, wHeight);
      bCtx.restore();

      // Gradient 3
      const g3 = bCtx.createRadialGradient(wWidth * 0.1, wHeight * 0.5, 0, wWidth * 0.1, wHeight * 0.5, wWidth * 0.5);
      g3.addColorStop(0, bgGradient3);
      g3.addColorStop(1, 'transparent');

      bCtx.beginPath();
      bCtx.fillStyle = g3;
      bCtx.fillRect(0, 0, wWidth, wHeight);
    };

    const createItems = () => {
      circles = [];
      lines = [];

      // Create Circles
      if (config.circle.amount > 0 && config.circle.layer > 0) {
        for (let i = 0; i < config.circle.amount / config.circle.layer; i++) {
          for (let j = 0; j < config.circle.layer; j++) {
            circles.push({
              x: M.random() * wWidth,
              y: M.random() * wHeight,
              radius: M.random() * (20 + j * 5) + (20 + j * 5),
              color: config.circle.color as ColorRGB,
              alpha: M.random() * 0.2 + (config.circle.alpha - j * 0.1),
              speed: config.speed * (1 + j * 0.5),
            });
          }
        }
      }

      // Create Lines
      if (config.line.amount > 0 && config.line.layer > 0) {
        for (let m = 0; m < config.line.amount / config.line.layer; m++) {
          for (let n = 0; n < config.line.layer; n++) {
            lines.push({
              x: M.random() * wWidth,
              y: M.random() * wHeight,
              width: M.random() * (20 + n * 5) + (20 + n * 5),
              color: config.line.color as ColorRGB,
              alpha: M.random() * 0.2 + (config.line.alpha - n * 0.1),
              speed: config.speed * (1 + n * 0.5),
            });
          }
        }
      }
    };

    // 4. Animation Loop
    const animate = () => {
      if (!fCtx) return;
      const sin = M.sin(degree);
      const cos = M.cos(degree);

      // Clear Foreground Canvas
      fCtx.clearRect(0, 0, wWidth, wHeight);

      // Draw Circles
      circles.forEach((item) => {
        let { x, y, radius, speed } = item;
        if (x > wWidth + radius) x = -radius;
        else if (x < -radius) x = wWidth + radius;
        else x += sin * speed;

        if (y > wHeight + radius) y = -radius;
        else if (y < -radius) y = wHeight + radius;
        else y -= cos * speed;

        item.x = x;
        item.y = y;
        drawCircle(x, y, radius, item.color, item.alpha);
      });

      // Draw Lines
      lines.forEach((item) => {
        let { x, y, width, speed } = item;
        if (x > wWidth + width * sin) x = -width * sin;
        else if (x < -width * sin) x = wWidth + width * sin;
        else x += sin * speed;

        if (y > wHeight + width * cos) y = -width * cos;
        else if (y < -width * cos) y = wHeight + width * cos;
        else y -= cos * speed;

        item.x = x;
        item.y = y;
        drawLine(x, y, width, item.color, item.alpha);
      });

      requestRef.current = requestAnimationFrame(animate);
    };

    // 5. Initialization
    const init = () => {
      setCanvasSize();
      createItems();
      drawBack();
      animate();
    };

    init();

    // 6. Resize Handler
    const handleResize = () => {
      setCanvasSize();
      createItems();
      drawBack();
    };

    window.addEventListener('resize', handleResize);

    // 7. Cleanup
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [props.themeRevision?.subTheme, bodyStyleVersion]);

  // Tailwind classes for structure, Custom class 'canvas-bg' for SCSS targeting
  return (
    <div id="canvas-bg" className="fixed left-0 top-0 -z-10 h-full w-full overflow-hidden bg-[#090d00]">
      <canvas ref={bgRef} className="absolute left-0 top-0 block" />
      <canvas ref={fgRef} className="absolute left-0 top-0 block" />
    </div>
  );
};

type ColorRGB = [number, number, number];

interface CircleParticle {
  x: number;
  y: number;
  radius: number;
  color: ColorRGB;
  alpha: number;
  speed: number;
}

interface LineParticle {
  x: number;
  y: number;
  width: number; // 'width' replaces 'radius' here
  color: ColorRGB;
  alpha: number;
  speed: number;
}

export default AnimatedParticleBackground;
